import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";

const ASSESSMENT_GENERATION_START_DELAY_MS = 2_000;

export interface StartAssessmentGenerationResponse {
  workflowId?: string;
}

export const useStartAssessmentGeneration = ({
  encounterId,
  motherEncounterId,
  patientId,
}: {
  encounterId?: string;
  patientId?: string;
  motherEncounterId?: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<StartAssessmentGenerationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.GENERATE(
        patientId,
        encounterId,
      ),
      mutationFn: async () => {
        const [response] = await Promise.all([
          axiosInstance.post<StartAssessmentGenerationResponse>(
            "/charting/ai-charting/assessment/start-generation",
            {
              encounterId,
              patientId,
            },
          ),
          new Promise((resolve) => {
            setTimeout(resolve, ASSESSMENT_GENERATION_START_DELAY_MS);
          }),
        ]);

        return response.data;
      },
      onSuccess: ({ workflowId }) => {
        if (!workflowId) {
          void queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.GENERATION_STATUS(
                patientId,
                encounterId,
              ),
          });
        }

        void queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId,
            encounterId,
          }),
        });
      },
    },
  });
};
